// TOLERATE-AND-SKIP is per FILE and per LAYER: one malformed file loses only itself, never the NO
// JD anywhere in this path: JD-sourced sessions never reach this scan — their boundaries are
// fetched server-side under credentials the FE must never hold.
import polygonClipping, {
  type MultiPolygon as PcMultiPolygon,
} from "polygon-clipping";
import { unzip } from "./zipRead";
import { readShapefile } from "./shpRead";
import { readKml } from "./kmlRead";
import { polygonAreaM2, polygonsOf } from "./samplingDesign";

const M2_PER_ACRE = 4046.86;
export const MIN_ACRES = 2.0;
export const MAX_ACRES = 2500.0;
// CONUS box, same constants as the server gate
const CONUS_LON: [number, number] = [-125.0, -66.9];
const CONUS_LAT: [number, number] = [24.4, 49.4];
/* * compressed-size skip (checked on File.size BEFORE any read) — boundary carriers are small */
export const MAX_SCAN_BYTES = 100 * 1024 * 1024;
/* * and a DECOMPRESSED budget per zip — a 5MB zip of yield CSVs can inflate to GBs (review) */
export const MAX_INFLATED_BYTES = 300 * 1024 * 1024;
/* * a layer with this many polygon PARTS is a ZONE SET until the server's classifier says otherwise */
const MANY_POLYGONS = 4;

// boundary tokens are the STRONG set only — the server treats `field` as a WEAK signal, and the
// review showed multi-polygon zone files named after their field ("Smith field zones.
export const BOUNDARY_TOKEN =
  /(^|[\s._-])(bnd|bound|boundary|boundaries)([\s._-]|$)/i;
export const ZONE_TOKEN =
  /(^|[\s._-])(zn|zns|zone|zones|mz|mgmt|mgmtzone|zoning|rx|prescription)([\s._-]|$)/i;

/* * File bytes with a FileReader fallback — jsdom (and some older WebViews) lack Blob.arrayBuffer. */
export function fileBytes(f: Blob): Promise<Uint8Array> {
  if (typeof f.arrayBuffer === "function")
    return f.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => reject(r.error ?? new Error("file read failed"));
    r.readAsArrayBuffer(f);
  });
}

export interface BoundaryCandidate {
  name: string;
  /* * WGS84, straight from the file — the server re-parses the same bytes later, so no reshaping */
  geojson: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  acres: number;
  sourceFile: string;
  /* * name came from the filename, not the data — the row deserves a rename nudge */
  autoNamed: boolean;
}

export interface BoundaryScanResult {
  candidates: BoundaryCandidate[];
  /* * files the scan looked inside */
  scanned: number;
  /* * files left entirely to the server chain, with the reason */
  skipped: { file: string; reason: string }[];
}

/* * Per-FILE scan output — cacheable by the caller (name:size key), combined by `combineScans`. */
export interface FileScan {
  candidates: BoundaryCandidate[];
  skipped: { file: string; reason: string }[];
  scanned: boolean;
}

/* * Trailing export-convention junk off a filename-derived name — mirrors compute's `_trim_name_junk`. */
export function nameFromFilename(filename: string): string {
  let base = (filename || "field").split("/").pop()!.split("\\").pop()!;
  base = base.replace(/\.(zip|kmz|kml|geojson|json|shp)$/i, "");
  const toks = base.split(/[\s._-]+/).filter(Boolean);
  while (
    toks.length > 1 &&
    (/^(bnd|bound|boundary|boundaries|exe|export|exp|final|new|v\d+)$/i.test(
      toks[toks.length - 1]!,
    ) ||
      /^\d{4,}$/.test(toks[toks.length - 1]!))
  ) {
    toks.pop();
  }
  return toks.join(" ") || "field";
}

function nameFromProps(
  p: Record<string, unknown> | null | undefined,
): string | null {
  for (const k of [
    "name",
    "Name",
    "NAME",
    "field",
    "Field",
    "FIELD",
    "field_name",
    "label",
    "title",
  ]) {
    const v = p?.[k];
    // a NUMERIC name ("1024") is a real name — plenty of farms number their ground (review)
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return null;
}

function isPolygonal(
  g: GeoJSON.Geometry | null | undefined,
): g is GeoJSON.Polygon | GeoJSON.MultiPolygon {
  return g?.type === "Polygon" || g?.type === "MultiPolygon";
}

/* * polygon PARTS — the zone-set gate counts these, not features (a MultiPolygon zone set is one feature) */
function partsOf(g: GeoJSON.Geometry): number {
  return g.type === "MultiPolygon" ? g.coordinates.length : 1;
}

function inConus(g: GeoJSON.Geometry): boolean {
  for (const poly of polygonsOf(g))
    for (const ring of poly)
      for (const [lon, lat] of ring) {
        if (
          lon < CONUS_LON[0] ||
          lon > CONUS_LON[1] ||
          lat < CONUS_LAT[0] ||
          lat > CONUS_LAT[1]
        )
          return false;
      }
  return true;
}

function bboxOf(g: GeoJSON.Geometry): [number, number, number, number] {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const poly of polygonsOf(g))
    for (const ring of poly)
      for (const [x, y] of ring) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
      }
  return [x0, y0, x1, y1];
}

function overlapPctMax(
  a: GeoJSON.Geometry,
  ab: [number, number, number, number],
  b: GeoJSON.Geometry,
  bb: [number, number, number, number],
): number {
  if (ab[2] < bb[0] || bb[2] < ab[0] || ab[3] < bb[1] || bb[3] < ab[1])
    return 0;
  try {
    const inter = polygonClipping.intersection(
      polygonsOf(a) as PcMultiPolygon,
      polygonsOf(b) as PcMultiPolygon,
    );
    if (!inter.length) return 0;
    const ai = polygonAreaM2({ type: "MultiPolygon", coordinates: inter });
    return (
      Math.max(
        ai / Math.max(1, polygonAreaM2(a)),
        ai / Math.max(1, polygonAreaM2(b)),
      ) * 100
    );
  } catch {
    // polygon-clipping throws on degenerate input — treat as no overlap; the server-side
    // check-overlap call before each create is the authority either way
    return 0;
  }
}

/* * the in-drop duplicate threshold — the server's own ≥1% GREATEST rule (migration 049) */
const DEDUP_PCT = 1.0;

export interface Layer {
  /* * the shp-set / kml doc / geojson file the polygons came from — the classification unit */
  source: string;
  features: GeoJSON.Feature[];
}

/*
 * One file → zero or more LAYERS of features (a zip can hold several shp sets + kmls).
 * Exported for `lib/dropIdentify` — the post-press identification screen classifies the SAME
 * layers this scan walks, without a second reader implementation.
 */
export async function layersOf(
  name: string,
  bytes: Uint8Array,
  budget: { left: number },
  depth = 0,
): Promise<Layer[]> {
  const lower = name.toLowerCase();
  const out: Layer[] = [];
  const text = () => new TextDecoder().decode(bytes);
  if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
    try {
      const gj = JSON.parse(text()) as GeoJSON.GeoJSON;
      const feats =
        gj.type === "FeatureCollection"
          ? Array.isArray(gj.features)
            ? gj.features.filter(
                (f): f is GeoJSON.Feature => !!f && f.type === "Feature",
              )
            : []
          : gj.type === "Feature"
            ? [gj]
            : gj.type
              ? [
                  {
                    type: "Feature",
                    properties: {},
                    geometry: gj as GeoJSON.Geometry,
                  } as GeoJSON.Feature,
                ]
              : [];
      out.push({ source: name, features: feats });
    } catch {
      /* not json — the server will name the failure */
    }
  } else if (lower.endsWith(".kml")) {
    out.push({ source: name, features: readKml(text()) });
  } else if (lower.endsWith(".kmz")) {
    for (const e of await unzip(bytes)) {
      budget.left -= e.bytes.length;
      if (budget.left < 0) break;
      if (e.name.toLowerCase().endsWith(".kml"))
        out.push({
          source: name,
          features: readKml(new TextDecoder().decode(e.bytes)),
        });
    }
  } else if (lower.endsWith(".zip")) {
    const entries = await unzip(bytes);
    // the DECOMPRESSED budget (review): a small zip of yield CSVs inflates to GBs — stop walking
    // when the budget is spent; whatever wasn't scanned is simply the server's to parse.
    for (const e of entries) budget.left -= e.bytes.length;
    if (budget.left < 0) return out;
    // group shapefile parts by their CASE-FOLDED basename (review: FIELD.SHP + field.dbf split)
    const byBase = new Map<
      string,
      { base: string; shp?: Uint8Array; dbf?: Uint8Array; prj?: string }
    >();
    for (const e of entries) {
      const el = e.name.toLowerCase();
      const key = el.replace(/\.(shp|dbf|prj|shx|cpg)$/i, "");
      if (/\.(shp|dbf|prj)$/i.test(el)) {
        const g = byBase.get(key) ?? {
          base: e.name.replace(/\.(shp|dbf|prj|shx|cpg)$/i, ""),
        };
        if (el.endsWith(".shp")) g.shp = e.bytes;
        else if (el.endsWith(".dbf")) g.dbf = e.bytes;
        else g.prj = new TextDecoder().decode(e.bytes);
        byBase.set(key, g);
      } else if (el.endsWith(".kml")) {
        out.push({
          source: `${name}/${e.name}`,
          features: readKml(new TextDecoder().decode(e.bytes)),
        });
      } else if (el.endsWith(".zip") && depth < 1) {
        // nested export zips are the Rogo convention — one level, same as the server walks
        out.push(
          ...(await layersOf(`${name}/${e.name}`, e.bytes, budget, depth + 1)),
        );
      }
    }
    for (const g of byBase.values()) {
      if (g.shp)
        out.push({
          source: `${name}/${g.base}.shp`,
          features: readShapefile({ shp: g.shp, dbf: g.dbf, prj: g.prj }),
        });
    }
  }
  return out;
}

/*
 *
 * Scan ONE file. Exported so the panel can cache per-file results (name:size key) instead of
 * re-parsing the whole manifest on every drop gesture (review: each add re-read every archive).
 * NEVER rejects: any parse failure inside skips that file/layer with a reason.
 */
export async function scanFile(f: {
  name: string;
  size?: number;
  bytes: () => Promise<Uint8Array>;
}): Promise<FileScan> {
  const candidates: BoundaryCandidate[] = [];
  const skipped: { file: string; reason: string }[] = [];
  const lower = f.name.toLowerCase();
  if (!/\.(zip|kmz|kml|geojson|json)$/.test(lower)) {
    return {
      candidates,
      skipped: [{ file: f.name, reason: "not a boundary-carrying format" }],
      scanned: false,
    };
  }
  if (typeof f.size === "number" && f.size > MAX_SCAN_BYTES) {
    return {
      candidates,
      skipped: [{ file: f.name, reason: "too large for the in-browser scan" }],
      scanned: false,
    };
  }
  let layers: Layer[];
  try {
    const bytes = await f.bytes();
    if (bytes.length > MAX_SCAN_BYTES) {
      return {
        candidates,
        skipped: [
          { file: f.name, reason: "too large for the in-browser scan" },
        ],
        scanned: false,
      };
    }
    layers = await layersOf(f.name, bytes, { left: MAX_INFLATED_BYTES });
  } catch (e) {
    console.warn(
      `[boundary-scan] ${f.name}: unreadable — left to the server`,
      e,
    );
    return {
      candidates,
      skipped: [{ file: f.name, reason: "unreadable — left to the server" }],
      scanned: false,
    };
  }
  for (const layer of layers) {
    try {
      const polys = layer.features.filter(
        (ft) => ft && isPolygonal(ft.geometry),
      );
      if (!polys.length) continue;
      const src = layer.source;
      const totalParts = polys.reduce(
        (n, ft) => n + partsOf(ft.geometry as GeoJSON.Geometry),
        0,
      );
      // THE CLASSIFICATION-LITE RULES (tightened by the review), in order of authority zone token
      // anywhere on the path → never boundaries here (the server's zone lane owns it)
      // ≥MANY_POLYGONS polygon PARTS without a STRONG boundary token → a zone set a MULTI-polygon
      // layer without a token needs PER-FEATURE NAMES (a real multi-field export names its fields
      // unnamed tiling polygons are zones) — the bare-polygon Add-Field parity applies to the
      // SINGLE-polygon layer only.
      if (ZONE_TOKEN.test(src)) {
        skipped.push({
          file: src,
          reason: "zone-set naming — left to the server",
        });
        continue;
      }
      const hasBnd = BOUNDARY_TOKEN.test(src);
      if (totalParts >= MANY_POLYGONS && !hasBnd) {
        skipped.push({
          file: src,
          reason: `${totalParts} polygons — likely a zone set, left to the server`,
        });
        continue;
      }
      if (
        polys.length > 1 &&
        !hasBnd &&
        !polys.every(
          (ft) =>
            nameFromProps(ft.properties as Record<string, unknown> | null) !=
            null,
        )
      ) {
        skipped.push({
          file: src,
          reason: `${polys.length} unnamed polygons — ambiguous, left to the server`,
        });
        continue;
      }
      const layerBase = src.split("/").pop() ?? src;
      for (const ft of polys) {
        try {
          const g = ft.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
          const acres = polygonAreaM2(g) / M2_PER_ACRE;
          if (
            !Number.isFinite(acres) ||
            acres < MIN_ACRES ||
            acres > MAX_ACRES
          ) {
            skipped.push({
              file: src,
              reason: `${Number.isFinite(acres) ? acres.toFixed(1) : "?"} acres — outside the ${MIN_ACRES}-${MAX_ACRES} ac field range`,
            });
            continue;
          }
          if (!inConus(g)) {
            skipped.push({
              file: src,
              reason: "outside the continental US — left to the server",
            });
            continue;
          }
          const propName = nameFromProps(
            ft.properties as Record<string, unknown> | null,
          );
          candidates.push({
            // the LAYER's own name, not the outer drop file's (review: nested exports mis-named)
            name: propName ?? nameFromFilename(layerBase),
            geojson: g,
            acres,
            sourceFile: src,
            autoNamed: propName == null,
          });
        } catch {
          skipped.push({
            file: src,
            reason: "a polygon could not be measured — left to the server",
          });
        }
      }
    } catch (e) {
      console.warn(
        `[boundary-scan] ${layer.source}: layer failed — left to the server`,
        e,
      );
      skipped.push({
        file: layer.source,
        reason: "unreadable layer — left to the server",
      });
    }
  }
  return { candidates, skipped, scanned: true };
}

/*
 *
 * Combine per-file scans: in-drop dedup (the server's own ≥1% GREATEST-overlap rule, largest-first
 * keep order so the result is deterministic and mirrors the chain), then de-duplicate NAMES with
 * " 2"/" 3" suffixes so a multi-boundary drop doesn't mint N identical rows.
 */
export function combineScans(scans: FileScan[]): BoundaryScanResult {
  const all = scans.flatMap((s) => s.candidates);
  const skipped = scans.flatMap((s) => s.skipped);
  const scanned = scans.filter((s) => s.scanned).length;
  const withBbox = all.map((c) => ({
    c,
    bbox: bboxOf(c.geojson),
    area: polygonAreaM2(c.geojson),
  }));
  withBbox.sort((a, b) => b.area - a.area); // keep-LARGEST, the chain's deterministic order
  const kept: typeof withBbox = [];
  for (const x of withBbox) {
    if (
      kept.some(
        (k) =>
          overlapPctMax(k.c.geojson, k.bbox, x.c.geojson, x.bbox) >= DEDUP_PCT,
      )
    )
      continue;
    kept.push(x);
  }
  const seen = new Map<string, number>();
  const candidates = kept.map(({ c }) => {
    const n = (seen.get(c.name) ?? 0) + 1;
    seen.set(c.name, n);
    return n === 1 ? c : { ...c, name: `${c.name} ${n}` };
  });
  return { candidates, scanned, skipped };
}

/* * The whole scan in one call (tests + simple callers); the panel uses scanFile + combineScans. */
export async function scanForBoundaries(
  files: { name: string; size?: number; bytes: () => Promise<Uint8Array> }[],
): Promise<BoundaryScanResult> {
  const scans: FileScan[] = [];
  for (const f of files) scans.push(await scanFile(f));
  return combineScans(scans);
}
